"use client"
import { Content, isFilled } from "@prismicio/client"
import { SliceComponentProps } from "@prismicio/react"
import { PrismicNextLink } from "@prismicio/next"
import {
  useForm,
  useStatus,
  useFormIsValid,
  useFormValues,
} from "@aura-design/system/form"
import { ALL_AIRPORTS } from "@airplan/utils/constants"
import { contactFormSchema } from "@airplan/utils/validator-schema"
import { sendContactForm } from "@/utils/contact"
import { PrismicRichText } from "@airplan/ui/prismic-richtext"
import Input from "@aura-design/system/input"
import Textarea from "@aura-design/system/textarea"
import Grid from "@aura-design/system/grid"
import Button from "@aura-design/system/button"
import Alert from "@aura-design/system/alert"
import Select from "@airplan/ui/select"
import Wave from "@airplan/ui/wave"

/**
 * Props for `FormContact`.
 */
export type FormContactProps = SliceComponentProps<Content.FormContactSlice>

/**
 * Component for "FormContact" Slices.
 */
const FormContact = ({ slice }: FormContactProps): JSX.Element => {
  const formData = useForm({
    fullName: "",
    email: "",
    airport: "",
    message: "",
    accept: false,
  })
  const status = useStatus()
  const { fullName, email, message, airport, accept } = formData
  const isValid = useFormIsValid(formData, contactFormSchema)
  const handleContactForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    status.resetStatus()
    if (!isValid) {
      for (const field in formData) {
        formData[field]?.setTouch(true)
      }
      return
    }
    status.setIsLoading(true)
    const formValues = useFormValues(formData)
    const res = await sendContactForm(formValues, "formContact")
    handleResponse(res)
  }
  
  const handleResponse = (res: any) => {
    if (res.status !== 200) {
      status.setIsError(true)
      status.setMessage("Ha ocurrido un error al enviar el correo electrónico")
      return
    }
    for (const field in formData) {
      formData[field]?.reset()
      formData[field]?.setTouch(false)
    }
    accept?.setValue(false)
    status.setMessage("Tu mensaje ha sido enviado")
    status.setSubmited(true)
    status.setIsLoading(false)
  }

  return (
    <section
      className="contact-form relative"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
    >
      <Grid col="two" className="gap-0">
        <div className="light-blue-side pt-6 content">
          <div className="smash p-4">
            <h2>
              {isFilled.richText(slice.primary.subtitle1) && (
                <PrismicRichText field={slice.primary.subtitle1} />
              )}
              <strong>
                {isFilled.richText(slice.primary.subtitle2) && (
                  <PrismicRichText field={slice.primary.subtitle2} />
                )}
              </strong>
            </h2>
            {isFilled.richText(slice.primary.paragraph) && (
              <PrismicRichText field={slice.primary.paragraph} />
            )}
          </div>
        </div>
        <div className="dark-blue-side bg-dark-blue pt-6 text-white">
          <div className="smash p-4">
            <form onSubmit={handleContactForm} className="relative z-20 mb-10">
              <h1>
                {isFilled.richText(slice.primary.title) && (
                  <PrismicRichText field={slice.primary.title} />
                )}
              </h1>
              <p>Los campos marcados con * son obligatorios.</p>
              <Grid col="two" className="mb-2 gap-2 pt-2">
                <div>
                  <label htmlFor="fullName">Nombre completo*</label>
                  <Input name="fullName" {...fullName} />
                </div>
                <div>
                  <label htmlFor="email">Correo electrónico*</label>
                  <Input {...email} />
                </div>
              </Grid>
              <div>
                <label htmlFor="airport">Aeropuerto*</label>
                <Select name="airport" placeholder="Selecciona" {...airport}>
                  {ALL_AIRPORTS.map((airport) => (
                    <option key={airport.value} value={airport.value}>
                      {airport.text}
                    </option>
                  ))}
                </Select>
              </div>
              <div>
                <label htmlFor="message">Mensaje*</label>
                <Textarea name="message" {...message} />
              </div>
              <p>
                Si buscas presentar una PQRSF, dirígete{" "}
                {isFilled.link(slice.primary.button_link) &&
                  isFilled.keyText(slice.primary.button_label) && (
                    <PrismicNextLink
                      field={slice.primary.button_link}
                      passHref
                      legacyBehavior
                    >
                      <a className="ml-0.5 cursor-pointer underline">
                        {slice.primary.button_label}
                      </a>
                    </PrismicNextLink>
                  )}
              </p>
              <label className="flex items-center inputer-m-0 my-2">
                <Input
                  className="default m-0 mr-1"
                  type="checkbox"
                  name="accept"
                  {...accept}
                  checked={!!accept?.value}
                  onChange={(e) => accept?.setValue(e.target.checked)}
                />
                Acepto la Política de Tratamiento de Datos Personales*
              </label>
              <div className="mt-2 flex justify-center">
                <Button
                  type="submit"
                  label={"Enviar"}
                  isLoading={status.state.isLoading}
                  isLoadingText="Enviando..."
                />
              </div>
              <div>
                <Alert state={status.state} isPushTop />
              </div>
            </form>
          </div>
        </div>
      </Grid>
      <div className="absolute bottom-0.5 left-0 right-0 z-10">
        <Wave color="text-aura-accents-1 " rotation={"180"} waveType="5" />
      </div>
    </section>
  )
}

export default FormContact
